import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/components/feature/AnimeCard';
import PlayerDemo from './components/PlayerDemo';
import EpisodeList from './components/EpisodeList';
import ShareModal from './components/ShareModal';
import { animeList } from '@/mocks/anime';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((item) => item.id === Number(id));
  const { isFavorite, toggleFavorite } = useFavorites();
  const [activeEpisode, setActiveEpisode] = useState(anime?.latestEpisode ?? 1);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    setActiveEpisode(anime?.latestEpisode ?? 1);
    setShareOpen(false);
    window.scrollTo({ top: 0 });
  }, [id, anime]);

  useDocumentMeta(
    anime ? `${anime.title}全集在线观看-动漫在线观看` : '动漫详情-动漫在线观看',
    anime
      ? `观看${anime.title}全集，最新高清动漫资源，支持在线播放及相关推荐查看。${anime.description}`
      : '动漫详情页',
    anime
      ? `${anime.title},${anime.title}在线观看,${anime.type}动漫,动漫全集,动漫在线观看`
      : '动漫在线观看',
  );

  if (!anime) {
    return (
      <div className="w-full px-4 md:px-8 py-20">
        <div className="max-w-lg mx-auto rounded-lg bg-background-50 border border-background-200 px-6 py-16 text-center">
          <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">未找到该动漫</h1>
          <p className="text-sm text-foreground-500 mb-6">
            该作品可能已被移除，或链接有误。返回首页继续浏览吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(anime.id);
  const related = animeList
    .filter((item) => item.id !== anime.id)
    .sort((a, b) => {
      const aScore = (a.type === anime.type ? 1 : 0) * 1000 + a.views;
      const bScore = (b.type === anime.type ? 1 : 0) * 1000 + b.views;
      return bScore - aScore;
    })
    .slice(0, 6);

  const metaItems = [
    { label: '类型', value: anime.type, to: `/category/${encodeURIComponent(anime.type)}` },
    { label: '地区', value: anime.region },
    { label: '年代', value: `${anime.year}年` },
    { label: '状态', value: anime.status },
    { label: '更新', value: anime.updateDate },
    { label: '评分', value: `${anime.rating.toFixed(1)} 分` },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: `${anime.type}动漫`, to: `/category/${encodeURIComponent(anime.type)}` },
          { label: anime.title },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2 space-y-6">
          <PlayerDemo cover={anime.cover} title={anime.title} episode={activeEpisode} />

          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {anime.title}
            </h1>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900 font-medium flex items-center gap-1">
                <i className="ri-star-fill text-[10px]"></i>
                {anime.rating.toFixed(1)}
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs bg-primary-50 text-primary-700">
                {anime.views.toFixed(1)}万播放
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-600">
                {anime.status === '已完结' ? `全 ${anime.episodes} 集` : `更新至第 ${anime.latestEpisode} 集`}
              </span>
              {anime.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-600">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <button
                type="button"
                onClick={() => toggleFavorite(anime.id)}
                className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  favorited
                    ? 'bg-accent-500 hover:bg-accent-600 text-background-50'
                    : 'bg-background-50 border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600'
                }`}
              >
                <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                {favorited ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              <button
                type="button"
                onClick={() => setActiveEpisode(Math.min(anime.episodes, activeEpisode + 1))}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-skip-forward-line"></i>
                下一集
              </button>
            </div>
          </div>

          <section className="rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-sm text-foreground-700 leading-relaxed">{anime.description}</p>
            <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap gap-x-8 gap-y-3">
              {metaItems.map((item) => (
                <div key={item.label} className="text-sm">
                  <span className="text-foreground-500">{item.label}：</span>
                  {item.to ? (
                    <Link to={item.to} className="text-primary-600 hover:text-primary-700 cursor-pointer">
                      {item.value}
                    </Link>
                  ) : (
                    <span className="text-foreground-800">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg bg-background-50 border border-background-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧集列表</h2>
              <span className="text-sm text-foreground-500">
                共 {anime.episodes} 集 · 已更新 {anime.latestEpisode} 集
              </span>
            </div>
            <EpisodeList
              episodes={anime.episodes}
              latestEpisode={anime.latestEpisode}
              activeEpisode={activeEpisode}
              onSelect={setActiveEpisode}
            />
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {related.map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
            <Link
              to={`/category/${encodeURIComponent(anime.type)}`}
              className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md bg-background-100 hover:bg-primary-50 hover:text-primary-600 text-foreground-700 font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              查看更多{anime.type}动漫
              <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>
        </aside>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={anime.title} />
    </div>
  );
}